import posthog from "posthog-js";
import { ref } from "vue";
import type { Router } from "vue-router";

type CartEvent = {
	searchText: string;
	page: number;
	rowsPerPage: number;
	isbn?: string;
	title: string;
	index: number;
};

type PosthogMethod = "identify" | "capture";

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
type PostHogEvent = [string, ...any[]];

export type Events =
	| {
			event: "Booklist Template download clicked";
	  }
	| {
			event: "Booklist Template download failed";
			error: string;
	  }
	| {
			event: "Booklist Template download succeeded";
	  }
	| {
			event: "Search Performed";
			data: {
				searchText: string;
				sortOptions?: {
					sortColumn:
						| "title"
						| "publicationYear"
						| "publisher"
						| "type"
						| "edition";
					sortDirection: "asc" | "desc";
				}[];
				filters?: Record<
					"publisher" | "edition" | "publicationYear" | "fileType",
					string[]
				>;
				totalMatches: number;
				page: number;
				rowsPerPage: number;
			};
	  }
	| {
			event: "Book Added To Cart";
			data: CartEvent;
	  }
	| {
			event: "Book Removed From Cart";
			data: CartEvent;
	  }
	| {
			event: "Booklist Edited";
			data: {
				item_count: number;
			};
	  };

export type PosthigInitInput = {
	debug: boolean;
	denyList: string[];
};

export function posthogInit({
	debug = false,
	denyList = [] as string[],
}: PosthigInitInput) {
	const forceLocal = import.meta.env.VITE_POSTHOG_LOCAL === "true";
	const denied =
		import.meta.env.VITE_PLAYWRIGHT === "true" || forceLocal
			? []
			: ["127.0.0.1", "localhost"].concat(denyList);
	if (!denied.includes(window.location.hostname)) {
		posthog.init(import.meta.env.VITE_POSTHOG_API_KEY, {
			api_host: import.meta.env.VITE_POSTHOG_ENDPOINT,
			disable_session_recording: true,
			enable_heatmaps: true,
			loaded() {
				for (const [method, event] of bibliu_queue) {
					posthog[method](...event);
				}
				bibliu_queue.length = 0;
			},
		});

		posthog.onFeatureFlags(() => {
			if (posthog.isFeatureEnabled("record-session")) {
				posthog.startSessionRecording();
			}
		});
	}
	posthog.debug(debug);
}

export function addFeatureFlagsToRouter(router: Router, { redirect = "/" }) {
	router.beforeEach((to) => {
		if (
			typeof to.meta?.featureFlag === "string" &&
			!isFeatureFlagEnabled(to.meta.featureFlag)
		) {
			return redirect;
		}
	});
}

export function addPageViewToRouter(router: Router) {
	router.afterEach((_to, _from, failure) => {
		if (!failure) {
			processPosthogMethod("capture", ["$pageview"]);
		}
	});
}

export const usePosthog =
	import.meta.env.VITE_POSTHOG_LOCAL === "true" ||
	(import.meta.env.VITE_PROD === "true" &&
		import.meta.env.VITE_PLAYWRIGHT !== "true");

/**
 * Checks if a feature flag is enabled.
 * @param key The feature flag key.
 * @param allowLocal Whether to allow the feature flag to be enabled in local development.
 */
export function isFeatureFlagEnabled(key: string, allowLocal = true) {
	return usePosthog ? Boolean(posthog.isFeatureEnabled(key)) : allowLocal;
}

/**
 * Returns a reactive ref that tracks whether a feature flag is enabled.
 * Unlike `isFeatureFlagEnabled`, this updates when PostHog flags load asynchronously.
 */
export function useFeatureFlag(key: string, allowLocal = true) {
	const enabled = ref(isFeatureFlagEnabled(key, allowLocal));
	if (usePosthog) {
		posthog.onFeatureFlags(() => {
			enabled.value = Boolean(posthog.isFeatureEnabled(key));
		});
	}
	return enabled;
}

export function capture({ event, ...properties }: Events) {
	posthog.capture(event, properties);
}

/**
 * Executes the specified method by calling it immediately if Posthog is loaded,
 * or queues the action if Posthog is not yet loaded.
 *
 * @param method - The Posthog method to execute.
 * @param event - The associated event data to process.
 */

export function processPosthogMethod(
	method: PosthogMethod,
	event: PostHogEvent,
) {
	if (posthog.__loaded) {
		posthog[method](...event);
	} else {
		bibliu_queue.push([method, event]);
	}
}

const bibliu_queue: [PosthogMethod, PostHogEvent][] = [];
